import { memo, useEffect, useState, useMemo } from 'react';
import { atom, useAtom, useAtomValue } from 'jotai';
import styled from 'styled-components';
import { Rnd } from 'react-rnd';
import maplibregl from 'maplibre-gl';
import { Image, Button, Tooltip, message } from 'antd';
import {
  PictureOutlined,
  CalendarOutlined,
  CompassOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CopyOutlined,
  DownloadOutlined,
  ExpandOutlined,
  InfoCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { API_URL_ADMIN } from '~/config/config';
import { projectMembersMapAtom } from '../../Member/MemberStore';
import DateTimeViewer from '~/components/common/DateTimeViewer';
import { useFreePhotoDownload } from '~/screens/FreePhotoScreen/hooks/useFreePhotoDownload';
import FreePhotoDetailModal from '../../panels/GeocollectDatatablePanel/tabs/FreePhotoDetailModal';
import type { FreePhoto_Base } from '~/common/typescript/models/FreePhoto';

export interface FreePhotoPopupProps {
  title?: string;
  lngLat?: maplibregl.LngLat | { lng: number; lat: number };
  properties: {
    uuid: string;
    file_name?: string;
    created_at?: string;
    compass_heading?: number;
    user_id?: number;
    project_id?: number;
    [key: string]: any;
  };
}

// Global function to trigger popup / panel
let globalShowFreePhotoPopup: ((props: FreePhotoPopupProps | null) => void) | null = null;

export const showFreePhotoPopup = (props: FreePhotoPopupProps | null) => {
  if (globalShowFreePhotoPopup) {
    globalShowFreePhotoPopup(props);
  } else {
    console.warn('FreePhotoPopup not initialized yet');
  }
};

export const freePhotoPopupPropsAtom = atom<FreePhotoPopupProps | null>(null);

const FreePhotoPopup = () => {
  const [props, setProps] = useAtom(freePhotoPopupPropsAtom);
  const projectMembersMap = useAtomValue(projectMembersMapAtom);
  const { downloadPhoto, downloading } = useFreePhotoDownload();

  const [previewVisible, setPreviewVisible] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);

  // Set global trigger
  useEffect(() => {
    globalShowFreePhotoPopup = setProps;
    return () => {
      globalShowFreePhotoPopup = null;
    };
  }, [setProps]);

  // Extract properties
  const properties = props?.properties;
  const uuid = properties?.uuid;
  const fileName = properties?.file_name || props?.title || 'Ảnh tự do';
  const createdAt = properties?.created_at;
  const compassHeading = properties?.compass_heading;
  const userId = properties?.user_id;

  const lat = props?.lngLat?.lat;
  const lng = props?.lngLat?.lng;
  const hasCoords = typeof lat === 'number' && typeof lng === 'number';

  // Calculate initial position near clicked point on map
  useEffect(() => {
    if (!props?.lngLat || !window.map) {
      if (props && !position) {
        setPosition({
          x: Math.max(20, window.innerWidth - 380),
          y: 90,
        });
      }
      return;
    }

    try {
      const point = window.map.project(props.lngLat);
      const panelWidth = 340;
      const panelHeight = 440;
      let x = point.x + 20;
      let y = point.y - 80;

      if (x + panelWidth > window.innerWidth - 20) {
        x = Math.max(20, point.x - panelWidth - 20);
      }
      if (y + panelHeight > window.innerHeight - 20) {
        y = Math.max(70, window.innerHeight - panelHeight - 20);
      }
      if (y < 70) y = 70;

      setPosition({ x, y });
    } catch {
      setPosition({
        x: Math.max(20, window.innerWidth - 380),
        y: 90,
      });
    }
  }, [props?.properties?.uuid, props?.lngLat]);

  // Resolved user
  const user = userId != null ? projectMembersMap[userId] : null;
  const userName = user
    ? user.fullname || user.username || `User #${userId}`
    : userId
    ? `User #${userId}`
    : null;

  const thumbnailUrl = uuid
    ? `${API_URL_ADMIN}/api/geocollect/restore/download-free-photo-file/${uuid}`
    : '';

  // Copy coordinates
  const handleCopyCoords = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasCoords) return;
    const text = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.success('Đã sao chép tọa độ vào clipboard');
      })
      .catch(() => {
        message.error('Không thể sao chép tọa độ');
      });
  };

  // Download photo
  const handleDownload = () => {
    if (!uuid) return;
    downloadPhoto(uuid, fileName);
  };

  // Close panel
  const handleClose = () => {
    setProps(null);
    setPreviewVisible(false);
    setIsDetailModalOpen(false);
  };

  // FreePhoto object for detail modal
  const photoObject: FreePhoto_Base | null = useMemo(() => {
    if (!uuid) return null;
    return {
      uuid,
      version: properties?.version || 1,
      file_name: fileName,
      created_at: createdAt,
      user_id: userId,
      project_id: properties?.project_id,
      is_deleted: false,
      metadata: JSON.stringify({
        location: hasCoords ? { coords: { latitude: lat, longitude: lng } } : undefined,
        compassHeading: compassHeading != null ? { trueHeading: compassHeading } : undefined,
      }),
    };
  }, [uuid, properties?.version, fileName, createdAt, userId, properties?.project_id, hasCoords, lat, lng, compassHeading]);

  if (!props) return null;

  return (
    <>
      <Rnd
        position={position || { x: Math.max(20, window.innerWidth - 380), y: 90 }}
        onDragStop={(e, d) => {
          setPosition({ x: d.x, y: d.y });
        }}
        dragHandleClassName="draggable-header"
        enableResizing={false}
        bounds="window"
        style={{ zIndex: 1000 }}
      >
        <UI_PanelWrapper>
          {/* Draggable Header */}
          <UI_Header className="draggable-header">
            <UI_HeaderLeft>
              <UI_Badge
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDetailModalOpen(true);
                }}
                title="Nhấn để xem chi tiết ảnh"
              >
                <PictureOutlined style={{ marginRight: 5, fontSize: 13 }} />
                <span>Ảnh tự do</span>
              </UI_Badge>
              <UI_Title title={fileName}>{fileName}</UI_Title>
            </UI_HeaderLeft>
            <UI_HeaderRight>
              <UI_CloseButton onClick={handleClose} title="Đóng panel">
                <CloseOutlined style={{ fontSize: 12 }} />
              </UI_CloseButton>
            </UI_HeaderRight>
          </UI_Header>

          {/* Panel Body */}
          <UI_Body>
            {/* Image Thumbnail with AntD Preview */}
            {thumbnailUrl && (
              <UI_ImageSection>
                <Image
                  src={thumbnailUrl}
                  alt={fileName}
                  width="100%"
                  height={160}
                  style={{ objectFit: 'cover', borderRadius: '6px' }}
                  preview={{
                    visible: previewVisible,
                    onVisibleChange: (visible) => setPreviewVisible(visible),
                  }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                />
              </UI_ImageSection>
            )}

            {/* Properties Section */}
            <UI_PropertiesSection>
              <UI_Properties_Wrapper>
                {/* Người chụp */}
                {userName && (
                  <UI_Properties_Row>
                    <UI_Properties_Label>
                      <UserOutlined style={{ color: '#3b82f6', fontSize: 13 }} />
                      <span>Người chụp</span>
                    </UI_Properties_Label>
                    <UI_Properties_Value style={{ fontWeight: 500 }}>
                      {userName}
                    </UI_Properties_Value>
                  </UI_Properties_Row>
                )}

                {/* Thời gian chụp */}
                {createdAt && (
                  <UI_Properties_Row>
                    <UI_Properties_Label>
                      <CalendarOutlined style={{ color: '#10b981', fontSize: 13 }} />
                      <span>Thời gian chụp</span>
                    </UI_Properties_Label>
                    <UI_Properties_Value>
                      <DateTimeViewer date={createdAt} initialMode="relative" />
                    </UI_Properties_Value>
                  </UI_Properties_Row>
                )}

                {/* Hướng la bàn */}
                {compassHeading != null && (
                  <UI_Properties_Row>
                    <UI_Properties_Label>
                      <CompassOutlined style={{ color: '#f59e0b', fontSize: 13 }} />
                      <span>Hướng la bàn</span>
                    </UI_Properties_Label>
                    <UI_Properties_Value>
                      {Number(compassHeading).toFixed(1)}&deg;
                    </UI_Properties_Value>
                  </UI_Properties_Row>
                )}

                {/* Tọa độ */}
                {hasCoords && (
                  <UI_Properties_Row>
                    <UI_Properties_Label>
                      <EnvironmentOutlined style={{ color: '#ef4444', fontSize: 13 }} />
                      <span>Tọa độ</span>
                    </UI_Properties_Label>
                    <UI_Properties_Value style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                      <span>
                        {lat.toFixed(6)}, {lng.toFixed(6)}
                      </span>
                      <Tooltip title="Sao chép tọa độ">
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined style={{ fontSize: 12, color: '#64748b' }} />}
                          style={{ padding: '0 2px', height: 'auto', lineHeight: 1 }}
                          onClick={handleCopyCoords}
                        />
                      </Tooltip>
                    </UI_Properties_Value>
                  </UI_Properties_Row>
                )}
              </UI_Properties_Wrapper>
            </UI_PropertiesSection>

            {/* Action Footer */}
            <UI_ActionFooter>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={downloading}
                onClick={handleDownload}
              >
                Tải ảnh
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<ExpandOutlined />}
                onClick={() => setIsDetailModalOpen(true)}
              >
                Chi tiết
              </Button>
            </UI_ActionFooter>
          </UI_Body>
        </UI_PanelWrapper>
      </Rnd>

      {/* Floating Metadata Panel when Image Preview is Open */}
      {previewVisible && (
        <UI_MetadataPanel>
          <UI_MetadataPanelTitle>
            <InfoCircleOutlined style={{ marginRight: 6 }} />
            <span>Thông tin ảnh</span>
          </UI_MetadataPanelTitle>
          {hasCoords && (
            <UI_MetadataSection>
              <UI_MetadataLabel>Tọa độ</UI_MetadataLabel>
              <UI_MetadataValue>
                {lat.toFixed(6)}, {lng.toFixed(6)}
              </UI_MetadataValue>
            </UI_MetadataSection>
          )}
          {compassHeading != null && (
            <UI_MetadataSection>
              <UI_MetadataLabel>Hướng la bàn</UI_MetadataLabel>
              <UI_MetadataValue>{Number(compassHeading).toFixed(1)}&deg;</UI_MetadataValue>
            </UI_MetadataSection>
          )}
          {createdAt && (
            <UI_MetadataSection>
              <UI_MetadataLabel>Thời gian</UI_MetadataLabel>
              <UI_MetadataValue>
                {new Date(createdAt).toLocaleString('vi-VN')}
              </UI_MetadataValue>
            </UI_MetadataSection>
          )}
          {userName && (
            <UI_MetadataSection>
              <UI_MetadataLabel>Người chụp</UI_MetadataLabel>
              <UI_MetadataValue>{userName}</UI_MetadataValue>
            </UI_MetadataSection>
          )}
        </UI_MetadataPanel>
      )}

      {/* Full Detail Modal */}
      {isDetailModalOpen && photoObject && (
        <FreePhotoDetailModal
          visible={isDetailModalOpen}
          photo={photoObject}
          photos={[photoObject]}
          onClose={() => setIsDetailModalOpen(false)}
        />
      )}
    </>
  );
};

export default memo(FreePhotoPopup);

/* ── Styled Components ─────────────────────────────────────────────────────── */

const UI_PanelWrapper = styled.div`
  width: 340px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const UI_Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  user-select: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const UI_HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const UI_HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const UI_CloseButton = styled.div`
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background-color: #fee2e2;
    color: #ef4444;
  }
`;

const UI_Body = styled.div`
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: calc(85vh - 80px);
  overflow-y: auto;
`;

const UI_Badge = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 3px 8px;
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
    color: #1d4ed8;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const UI_Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UI_ImageSection = styled.div`
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  line-height: 0;

  .ant-image {
    width: 100%;
    display: block;
  }

  .ant-image-img {
    display: block;
    width: 100%;
    height: 160px;
    object-fit: cover;
  }
`;

const UI_PropertiesSection = styled.div``;

const UI_Properties_Wrapper = styled.div`
  max-height: 220px;
  overflow-y: auto;
`;

const UI_Properties_Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
`;

const UI_Properties_Label = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-right: 12px;
  font-weight: 500;
  min-width: 90px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const UI_Properties_Value = styled.div`
  font-size: 12px;
  color: #374151;
  text-align: right;
  word-break: break-word;
`;

const UI_ActionFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
`;

const UI_MetadataPanel = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 240px;
  max-height: 400px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(20px);
  border-radius: 6px;
  padding: 12px;
  z-index: 9999;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    width: 200px;
    bottom: 10px;
    right: 10px;
  }
`;

const UI_MetadataPanelTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
`;

const UI_MetadataSection = styled.div`
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const UI_MetadataLabel = styled.div`
  font-size: 9px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const UI_MetadataValue = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.4;
  word-break: break-word;
  font-weight: 400;
`;
